import React from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import { useFeatureFlag } from "../../context/FeatureFlagContext";
import { LiveChatOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/LiveChatOutlined";
import { shouldShowMIAWChat } from "./utils";
import { trackMonetateEvent } from "../../context/MonetateContext";

import styles from "./live-chat-button.module.scss";

const LiveChatButton = React.memo(
  ({ startButtonText = "Start chat", buttonLocation, channel, className, showChatIcon }) => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const submittingTimeOut = 5000;

    const showMIAWChat = useFeatureFlag("show_miaw_chat") || shouldShowMIAWChat(channel, buttonLocation);

    React.useEffect(() => {
      const onStartSuccessful = event => {
        event.preventDefault();
        setIsSubmitting(false);
      };
      const onMIAWInitFailure = event => {
        event.preventDefault();

        showMIAWChat ? initialiseMIAWChat() : initialiseLiveChat();
      };
      window.addEventListener("jl-digital-conversations-chat-started-successfully", onStartSuccessful);
      window.addEventListener("jl-MIAW-conversations-chat-initialisation-failed", onMIAWInitFailure);
      return () => {
        window.removeEventListener("jl-digital-conversations-chat-started-successfully", onStartSuccessful);
        window.removeEventListener("jl-MIAW-conversations-chat-initialisation-failed", onMIAWInitFailure);
      };
    }, []);

    React.useEffect(() => {
      // This was to satisfy the linter of consistent return, this cleanup by definition does nothing
      if (!isSubmitting) return () => {};

      const interval = setTimeout(() => {
        setIsSubmitting(false);
      }, submittingTimeOut);

      return () => {
        clearInterval(interval);
      };
    }, [isSubmitting]);

    const getBreadcrumbFromButtonLocation = buttonLocation => {
      const subLocations = buttonLocation.split(" | ");
      const breadCrumbCategory = subLocations[1];
      const breadCrumbSubCategory = subLocations.pop();

      switch (breadCrumbCategory) {
        case "deliveryQueries":
          return breadCrumbCategory;
        case "returnsAndRefunds":
          return breadCrumbSubCategory === "returningOrExchanging"
            ? `${breadCrumbCategory}-returning`
            : `${breadCrumbCategory}-refunds`;
        case "problemsWithProduct":
          return breadCrumbSubCategory === "other" ? `${breadCrumbCategory}-other` : `${breadCrumbCategory}-electrical`;
        case "productStockInformation":
        case "placeEditCancelOrder":
        case "myJLMembership":
          return breadCrumbCategory;
        case "myAccountOnline":
          return "myJLMembership";
        case "repair":
          return "problemsWithProduct-electrical";

        default:
          return breadCrumbCategory;
      }
    };

    const initialiseMIAWChat = () => {
      window.dispatchEvent(
        new CustomEvent("jl-MIAW-conversations-start-chat", {
          detail: {
            channel,
            buttonLocation: getBreadcrumbFromButtonLocation(buttonLocation),
          },
        }),
      );
    };

    const initialiseLiveChat = () => {
      window.dispatchEvent(
        new CustomEvent("jl-digital-conversations-start-chat", {
          detail: {
            channel,
            buttonLocation,
          },
        }),
      );
    };

    const dispatchStartEvent = React.useCallback(event => {
      event.preventDefault();
      setIsSubmitting(true);
      trackMonetateEvent("live_chat_engagement_rate");
      showMIAWChat ? initialiseMIAWChat() : initialiseLiveChat();
    }, []);

    return (
      <button
        type="button"
        data-test="live-chat-button"
        className={classnames(styles["live-chat-button"], className, { [styles.disabled]: isSubmitting })}
        onClick={dispatchStartEvent}
      >
        <div className={classnames({ [styles["live-chat-button-loading"]]: isSubmitting })} />
        <span className={classnames({ [styles.hidden]: isSubmitting, [styles.showChatIcon]: showChatIcon })}>
          {showChatIcon && <LiveChatOutlined />}
          {startButtonText}
        </span>
      </button>
    );
  },
);

LiveChatButton.propTypes = {
  startButtonText: PropTypes.string,
  buttonLocation: PropTypes.string,
  channel: PropTypes.string,
  className: PropTypes.string,
  showChatIcon: PropTypes.bool,
};

LiveChatButton.defaultProps = {
  startButtonText: "Start chat",
  buttonLocation: undefined,
  channel: undefined,
  className: "",
  showChatIcon: false,
};

export default LiveChatButton;
